import { FacebookApi, FacebookFeedItem } from '@/api/FacebookApi';
import { GoogleBusinessFeedItem, GoogleMyBusinessApi } from '@/api/GoogleMyBusinessApi';
import { InstagramApi, InstagramFeedItem } from '@/api/InstagramApi';
import { LinkedInApi, LinkedInFeedItem } from '@/api/LinkedInApi';
import { SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { SocialFeedItem, SocialFeedPlatform } from '@/types/socialFeed';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Bot, DEFAULT_TIMEZONE, SupportedPlatform } from 'ignite360-core';

export const SOCIAL_FEED_PLATFORMS: SocialFeedPlatform[] = [
  SupportedPlatform.Instagram,
  SupportedPlatform.FacebookMessenger,
  SupportedPlatform.LinkedIn,
  SupportedPlatform.GoogleBusiness,
];

export const GLOBAL_SOCIAL_FEED_PAGE_SIZE = 20;
export const PROJECT_SOCIAL_FEED_PAGE_SIZE = 5;

function publishedAtTimestamp(publishedAt?: string | null): number {
  if (!publishedAt) {
    return 0;
  }
  const timestamp = new Date(publishedAt).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export function socialFeedPlatformLabel(platform: SocialFeedPlatform): string {
  if (platform === SupportedPlatform.FacebookMessenger) {
    return 'Facebook';
  }
  if (platform === SupportedPlatform.GoogleBusiness) {
    return 'Google Business';
  }
  if (platform === SupportedPlatform.Instagram) {
    return 'Instagram';
  }
  if (platform === SupportedPlatform.LinkedIn) {
    return 'LinkedIn';
  }
  return 'Platform';
}

export function paginateSocialFeedItems<T>(items: T[], page: number, pageSize: number): T[] {
  const startIndex = (page - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
}

export function getSocialFeedTotalPages(itemCount: number, pageSize: number): number {
  if (!itemCount) {
    return 1;
  }
  return Math.ceil(itemCount / pageSize);
}

export function clampSocialFeedPage(page: number, itemCount: number, pageSize: number): number {
  return Math.min(Math.max(page, 1), getSocialFeedTotalPages(itemCount, pageSize));
}

export function getConnectedSocialFeedPlatforms(bot: Bot): SocialFeedPlatform[] {
  return SOCIAL_FEED_PLATFORMS.filter((platform) => {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return !!meta?.isConnected?.(bot);
  });
}

export function interleaveFeedItems(itemGroups: SocialFeedItem[][]): SocialFeedItem[] {
  if (!itemGroups.length) {
    return [];
  }
  const maxLength = Math.max(...itemGroups.map((group) => group.length));
  const merged: SocialFeedItem[] = [];
  for (let index = 0; index < maxLength; index += 1) {
    for (const group of itemGroups) {
      const item = group[index];
      if (item) {
        merged.push(item);
      }
    }
  }
  return merged;
}

export function mergeAndSortFeedItems(itemGroups: SocialFeedItem[][]): SocialFeedItem[] {
  return itemGroups
    .flat()
    .sort(
      (left, right) =>
        publishedAtTimestamp(right.publishedAt) - publishedAtTimestamp(left.publishedAt),
    );
}

export function formatSocialFeedPublishedDate(
  publishedAt: string | null | undefined,
  timezone?: string | null,
): string | null {
  if (!publishedAt) {
    return null;
  }
  const tz = timezone || DEFAULT_TIMEZONE;
  try {
    const zonedDate = utcToZonedTime(new Date(publishedAt), tz);
    if (Number.isNaN(zonedDate.getTime())) {
      return null;
    }
    return format(zonedDate, 'd MMM yyyy, h:mm a');
  } catch {
    return null;
  }
}

export function formatSocialFeedPostMeta(
  publishedAt: string | null | undefined,
  platform: SocialFeedPlatform,
  timezone?: string | null,
): string | null {
  const dateText = formatSocialFeedPublishedDate(publishedAt, timezone);
  if (!dateText) {
    return null;
  }
  return `${dateText} on ${socialFeedPlatformLabel(platform)}`;
}

export function formatSocialFeedGlobalPostMeta(
  publishedAt: string | null | undefined,
  platform: SocialFeedPlatform,
  botName: string,
  timezone?: string | null,
): string | null {
  const dateText = formatSocialFeedPublishedDate(publishedAt, timezone);
  const platformName = socialFeedPlatformLabel(platform);
  const projectName = (botName || '').trim();
  if (dateText && projectName) {
    return `${dateText} · ${projectName} on ${platformName}`;
  }
  if (dateText) {
    return `${dateText} on ${platformName}`;
  }
  if (projectName) {
    return `${projectName} on ${platformName}`;
  }
  return `on ${platformName}`;
}

export function getBotsWithConnectedSocialPlatforms(bots: Bot[]): Bot[] {
  return bots.filter((bot) => getConnectedSocialFeedPlatforms(bot).length > 0);
}

export interface SocialFeedFetchResult {
  platform: SocialFeedPlatform;
  items: SocialFeedItem[];
  error?: boolean;
}

export async function fetchSocialFeedItems(
  botId: string,
  platform: SocialFeedPlatform,
): Promise<SocialFeedItem[]> {
  if (platform === SupportedPlatform.Instagram) {
    const rows = await InstagramApi.getRecentMedia(botId);
    return rows.map((row: InstagramFeedItem) => ({
      ...row,
      platform,
    }));
  }
  if (platform === SupportedPlatform.LinkedIn) {
    const rows = await LinkedInApi.getRecentPosts(botId);
    return rows.map((row: LinkedInFeedItem) => ({
      ...row,
      platform,
    }));
  }
  if (platform === SupportedPlatform.GoogleBusiness) {
    const rows = await GoogleMyBusinessApi.getRecentPosts(botId);
    return rows.map((row: GoogleBusinessFeedItem) => ({
      ...row,
      platform,
      mediaSourceUrl: row.thumbnailUrl || null,
      thumbnailUrl: row.thumbnailUrl
        ? GoogleMyBusinessApi.buildProxyMediaUrl(botId, row.thumbnailUrl) || null
        : null,
    }));
  }
  const rows = await FacebookApi.getRecentPosts(botId);
  return rows.map((row: FacebookFeedItem) => ({
    ...row,
    platform,
  }));
}

export async function fetchAllSocialFeedItems(bot: Bot): Promise<{
  items: SocialFeedItem[];
  results: SocialFeedFetchResult[];
}> {
  const platforms = getConnectedSocialFeedPlatforms(bot);
  const results = await Promise.all(
    platforms.map(async (platform): Promise<SocialFeedFetchResult> => {
      try {
        const items = await fetchSocialFeedItems(bot.id, platform);
        return { platform, items };
      } catch {
        return { platform, items: [], error: true };
      }
    }),
  );
  const successfulGroups = results.filter((result) => !result.error).map((result) => result.items);
  return {
    items: mergeAndSortFeedItems(successfulGroups),
    results,
  };
}

export async function fetchAllSocialFeedItemsForBots(bots: Bot[]): Promise<{
  items: SocialFeedItem[];
  partialLoadError: boolean;
  hasEligibleBots: boolean;
}> {
  const eligibleBots = getBotsWithConnectedSocialPlatforms(bots);
  if (!eligibleBots.length) {
    return { items: [], partialLoadError: false, hasEligibleBots: false };
  }

  const botResults = await Promise.all(
    eligibleBots.map(async (bot) => {
      const { items, results } = await fetchAllSocialFeedItems(bot);
      return {
        items: items.map((item) => ({
          ...item,
          botId: bot.id,
          botName: bot.name || 'Untitled project',
          botTimezone: bot.timezone,
        })),
        platformErrors: results.some((result) => result.error),
      };
    }),
  );

  return {
    items: mergeAndSortFeedItems(botResults.map((result) => result.items)),
    partialLoadError: botResults.some((result) => result.platformErrors),
    hasEligibleBots: true,
  };
}
